/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';
import initBem from 'js/lib/bem';

import { Button, Grid, Link, Typography, Typography2 } from '@coursera/cds-core';

import CopyrightV2 from 'bundles/page-footer/components/seo-sub-footer/CopyrightV2';
import SocialMediaLinksV2 from 'bundles/page-footer/components/seo-sub-footer/SocialMediaLinksV2';
import HeaderLogo from 'bundles/page-header/components/HeaderLogo';
import PageNavLink from 'bundles/page-header/components/page-navigation/PageNavLink';
import PageNavigation from 'bundles/page-header/components/page-navigation/PageNavigation';

import HeroImagePng from 'images/coding-challenges/HeroImage.png';

import _t from 'i18n!nls/coding-challenges';

import LandingPageCodingChallenges from './LandingPageCodingChallenges';
import LandingPageEligibility from './LandingPageEligibility';
import LandingPageFAQ from './LandingPageFAQ';
import LandingPageHowItWorks from './LandingPageHowItWorks';
import LandingPageRewards from './LandingPageRewards';

const styles = {
  headerText: css`
    color: var(--cds-color-blue-700);
    font-weight: bold;
  `,
  sections: css`
    margin-right: 60px;
    margin-left: 60px;
    margin-top: 60px;
    justify-content: center;
    scroll-margin-top: 100px;
  `,
  faqSections: css`
    margin: 60px;
    justify-content: center;
    scroll-margin-top: 70px;
  `,
  home: css`
    background-color: var(--cds-color-blue-50);
    padding: 50px;
    display: flex;
    flex-direction: row;
  `,
  heroText: css`
    margin: 40px;
  `,
  navBar: css`
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 100;
    padding: 14px 60px;
    background-color: var(--cds-color-neutral-background-primary);
    border-bottom: 1px solid var(--cds-color-grey-200);
    transition: all 0.3s ease;

    nav li {
      align-self: center;
    }

    nav li:last-child {
      margin-left: auto;
    }

    nav li:last-child a {
      color: white !important;
    }

    nav a {
      display: flex;
      color: var(--cds-color-neutral-primary) !important;
      font-size: 16px;
      font-weight: 600;
      line-height: 130%;
    }
  `,
  background: css`
    padding-top: 60px;
    padding-bottom: 60px;
    background-color: var(--cds-color-neutral-background-primary-weak);
    justify-content: center;
    scroll-margin-top: 70px;
  `,
  bottomDesktop: css`
    margin: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  headerRegisterButton: css`
    margin-left: auto;
  `,
  anchor: css`
    scroll-margin-top: 100px;
  `,
};

const LandingPageHome = () => {
  const bem = initBem('SeoGlobalFooter');

  return (
    <div aria-live="polite">
      <div id="navBar" css={styles.navBar}>
        <PageNavigation>
          <HeaderLogo />
          <PageNavLink label={_t('Home')} name="home" href="#home" />
          <PageNavLink label={_t('Eligibility')} name="eligibility" href="#eligibility" css={styles.anchor} />
          <PageNavLink label={_t('How It Works')} name="how_it_works" href="#howItWorks" />
          <PageNavLink label={_t('Coding Challenges')} name="coding_Challenges" href="#codingChallenges" />
          <PageNavLink label={_t('Rewards')} name="rewards" href="#rewards" />
          <PageNavLink label={_t('FAQ')} name="faq" href="#faq" />
          <Button variant="primary" size="medium" css={styles.headerRegisterButton}>
            <Link
              invert
              href={`${config.url.base}projects/data-science-coding-challenge-loan-default-prediction`}
              variant="quiet"
            >
              {_t('Register')}
            </Link>
          </Button>
        </PageNavigation>
      </div>
      <div id="home" css={styles.home}>
        <Grid item md={6} css={styles.heroText}>
          <Typography variant="d2semibold" css={styles.heroText}>
            {_t("Coursera's Data Science Coding Challenge")}
          </Typography>
          <Typography2 component="p" variant="bodyPrimary" css={styles.heroText}>
            {_t(
              'Join our latest Data Science competition and put your skills to the test by building the most accurate prediction model. In this challenge, you will work with real-world data, get actionable feedback, and compare your performance against other participants. Once complete, you’ll have the opportunity to showcase your work to potential employers.'
            )}
          </Typography2>
          <Button variant="primary" size="medium" css={styles.heroText}>
            <Link
              invert
              href={`${config.url.base}projects/data-science-coding-challenge-loan-default-prediction`}
              variant="quiet"
            >
              {_t('Register today!')}
            </Link>
          </Button>
        </Grid>
        <Grid item md={6} css={styles.heroText}>
          <img
            css={styles.heroText}
            src={HeroImagePng}
            alt="Woman with headphones looking at laptop, smiling with her hands on her mouth."
            width={598}
            height={405}
          />
        </Grid>
      </div>
      <div id="eligibility" css={styles.sections}>
        <LandingPageEligibility />
      </div>
      <div id="howItWorks" css={styles.sections}>
        <LandingPageHowItWorks />
      </div>
      <div id="codingChallenges" css={styles.background}>
        <LandingPageCodingChallenges />
      </div>
      <div id="rewards" css={styles.sections}>
        <LandingPageRewards css={[styles.anchor, styles.heroText]} />
      </div>
      <div id="faq" css={styles.faqSections}>
        <LandingPageFAQ />
      </div>
      <div className={bem('social-media-and-copyright', '', 'lohp-rebrand')} css={styles.bottomDesktop}>
        <CopyrightV2 />
        <SocialMediaLinksV2 />
      </div>
    </div>
  );
};

export default LandingPageHome;
